<template>
    <div class="apprentice-simple-card">
        <div class="selected-cohort">
            <div class="module-header">
                <h5 class="title">{{ sluCourseIn.course.name }} - {{ selectedCohort.name }}</h5>

                <b-form-select
                    class="apprentice-primary-select with-border cohort-season-select"
                    v-model="selectedCohortId"
                    :options="cohortOptions">
                </b-form-select>
                <b-form-select
                    class="apprentice-primary-select with-border cohort-course-select"
                    v-model="selectedCourse"
                    :options="courseOptions"
                    @change="changeCourse()">
                </b-form-select>
            </div>
            <div class="cohort-additional-info">
                <div class="cohort-progress">
                    <span class="label">{{ $t('slu_dashboard.progress_modules') }}</span>
                    <span class="amount">
                        {{
                            `${selectedCohort.completedPoints}/${selectedCohort.totalPoints} ${sluSchoolSettingsIn.name_points}`
                        }}
                    </span>
                    <span
                        class="bonus"
                        v-if="selectedCohort.totalBonusPoints > 0">
                        {{
                            `+ optioneel ${selectedCohort.completedBonusPoints}/${selectedCohort.totalBonusPoints} ${sluSchoolSettingsIn.name_points}`
                        }}
                    </span>
                </div>

                <div class="cohort-required">
                    <span class="label">{{ $t('slu_dashboard.required') }}</span>
                    <span class="amount">{{ calculateRequiredSluPoints() }}</span>
                </div>

                <div class="seasonal-progress">
                    <span class="label">{{ $t('slu_dashboard.grade_progress') }}</span>
                    <span class="seasonal-percentage"
                        >We zijn op {{ currentPercentage(selectedCohort.season) }}% van het jaar</span
                    >
                </div>
            </div>

            <div class="row modules">
                <div
                    class="col-21 col-md-6 col-xxl-3 module"
                    v-for="module of selectedCohort.modules"
                    v-if="selectedCohort.modules.length > 0"
                    :key="module.id">
                    <design-2021-slu-student-course-module-card-component
                        :module-in="module"
                        :slu-record-in="getSluModuleRecord(module)"
                        :slu-status-tags-in="statusOptions"
                        :slu-school-settings-in="sluSchoolSettingsIn"
                        @update-status="updateStudentModuleSatus"
                        @open-modal="openModuleModal">
                    </design-2021-slu-student-course-module-card-component>
                </div>

                <div
                    class="not-found with-header modules-not-found"
                    v-else>
                    <div class="icon">
                        <i class="fas fa-exclamation-circle"></i>
                    </div>
                    <div class="alert-notice">{{ $t('slu_dashboard.no_modules_found') }}</div>
                </div>
            </div>
        </div>

        <design-2021-slu-module-modal-component
            ref="slu-module-modal"
            :slu-status-tags-in="statusOptions"
            :slu-school-settings-in="sluSchoolSettingsIn"
            @update-status="updateStudentModuleSatus">
        </design-2021-slu-module-modal-component>
    </div>
</template>

<script>
import moment from 'moment/moment';

export default {
    name: 'design-2021-slu-student-course-component',
    props: ['sluStudentIn', 'sluCourseIn', 'sluStatusTagsIn', 'otherSluCoursesIn', 'sluSchoolSettingsIn'],
    data() {
        return {
            selectedCourse: this.sluCourseIn.id,
            selectedCohortId: this.sluStudentIn.slu_cohorts[0].id,
            sluStudent: this.sluStudentIn,
        };
    },
    mounted() {
        this.selectedCohortId = this.cohorts[0].id;
    },
    computed: {
        cohorts() {
            return this.sluStudent.slu_cohorts
                .map((cohort) => {
                    let totalHours = cohort.modules
                        .map((module) => {
                            module.user_id = this.sluStudent.id;
                            return module.required ? module.hours : 0;
                        })
                        .reduce((partialSum, a) => partialSum + a, 0);

                    let totalBonusHours = cohort.modules
                        .map((module) => {
                            module.user_id = this.sluStudent.id;
                            return !module.required ? module.hours : 0;
                        })
                        .reduce((partialSum, a) => partialSum + a, 0);

                    let completedHours = 0;
                    let completedBonusHours = 0;
                    cohort.modules.forEach((module) => {
                        if (module.slu_module_records) {
                            module.slu_module_records.forEach((record) => {
                                if (record.completed) {
                                    if (module.required) {
                                        completedHours += module.hours;
                                    } else {
                                        completedBonusHours += module.hours;
                                    }
                                }
                            });
                        }
                    });
                    const percentage = Math.round((completedHours / totalHours) * 100);
                    const totalPoints = totalHours / this.sluSchoolSettingsIn.hours_to_point;
                    const completedPoints = completedHours / this.sluSchoolSettingsIn.hours_to_point;
                    const totalBonusPoints = totalBonusHours / this.sluSchoolSettingsIn.hours_to_point;
                    const completedBonusPoints = completedBonusHours / this.sluSchoolSettingsIn.hours_to_point;

                    return {
                        id: cohort.id,
                        season_id: cohort.season_id,
                        name: cohort.name,
                        user_id: this.sluStudent.id,
                        status: null,
                        percentage: percentage,
                        season: cohort.season,
                        target: cohort.target,
                        totalHours: totalHours,
                        completedHours: completedHours,
                        completedBonusHours: completedBonusHours,
                        modules: cohort.modules,
                        cohort: cohort,
                        totalPoints,
                        completedPoints,
                        totalBonusPoints,
                        completedBonusPoints,
                    };
                })
                .sort(function (a, b) {
                    return a.season_id - b.season_id;
                })
                .reverse();
        },
        selectedCohort() {
            return this.cohorts.find((cohort) => cohort.id === this.selectedCohortId);
        },
        courseOptions() {
            return this.otherSluCoursesIn.map((course) => {
                return {
                    value: course.id,
                    text: course.course.name,
                };
            });
        },
        cohortOptions() {
            return this.sluStudent.slu_cohorts.map((cohort) => {
                return {
                    value: cohort.id,
                    text: cohort.name,
                };
            });
        },

        statusOptions() {
            if (this.sluStatusTagsIn) {
                return this.createStatusOptions();
            }

            return [];
        },
    },

    methods: {
        openModuleModal(module) {
            this.$refs['slu-module-modal'].show(module);
        },

        currentPercentage(season) {
            const now = moment();
            const seasonStart = moment(season.start).add(1, 'M');
            const seasonEnd = moment(seasonStart).add(10, 'M');
            if (now.isBefore(seasonStart)) {
                return 0;
            } else if (now.isAfter(seasonEnd)) {
                return 100;
            } else {
                const difference = seasonEnd - seasonStart;
                return Math.round((now.diff(seasonStart) / difference) * 100);
            }
        },

        changeCourse() {
            if (this.$authUser.isAdmin() || this.$authUser.isTeacher()) {
                window.location.href = this.routes.sluStudentCourse(this.sluStudentIn.id, this.selectedCourse);
            } else {
                window.location.href = this.routes.studentSluCourse(this.selectedCourse);
            }
        },
        createStatusOptions() {
            let Tags = this.sluStatusTagsIn;
            const hasCompleted = Tags.slice()
                .reverse()
                .find((x) => x.finished == true);
            if (Tags.length && hasCompleted) {
                Tags.push({
                    id: 'default_not_completed',
                    name: this.sluSchoolSettingsIn.name_unfinished
                        ? this.sluSchoolSettingsIn.name_unfinished
                        : this.$t('slu_dashboard.unfinished'),
                    color: this.sluSchoolSettingsIn.color_unfinished
                        ? this.sluSchoolSettingsIn.color_unfinished
                        : '#999999',
                });
                return Tags;
            } else if (!Tags.length) {
                return [
                    {
                        id: 'default_not_completed',
                        name: this.sluSchoolSettingsIn.name_unfinished
                            ? this.sluSchoolSettingsIn.name_unfinished
                            : this.$t('slu_dashboard.unfinished'),
                        color: this.sluSchoolSettingsIn.color_unfinished
                            ? this.sluSchoolSettingsIn.color_unfinished
                            : '#999999',
                    },
                    {
                        id: 'default_completed',
                        name: this.$t('slu_dashboard.finished'),
                        color: '#66bb88',
                    },
                ];
            } else {
                Tags.push({
                    id: 'default_not_completed',
                    name: this.sluSchoolSettingsIn.name_unfinished
                        ? this.sluSchoolSettingsIn.name_unfinished
                        : this.$t('slu_dashboard.unfinished'),
                    color: this.sluSchoolSettingsIn.color_unfinished
                        ? this.sluSchoolSettingsIn.color_unfinished
                        : '#999999',
                });
                Tags.push({
                    id: 'default_completed',
                    name: this.$t('slu_dashboard.finished'),
                    color: '#66bb88',
                });

                return Tags;
            }

            return [];
        },

        updateStudentModuleSatus(module, status, feedback = null) {
            this.alert.saving();
            let data = {
                userId: module.user_id,
                moduleId: module.id,
                cohortId: module.slu_cohort_id,
                courseId: this.sluCourseIn.id,
            };
            if (status.id === 'default_not_completed') {
                data.completed = false;
                data.statusTagId = null;
            } else if (status.id === 'default_completed') {
                data.completed = true;
            } else {
                data.statusTagId = status.id;
                data.completed = status.finished;
            }

            if (feedback) {
                data.feedback = feedback;
            }

            axios.post(this.routes.sluModuleRecord(), data).then(
                (response) => {
                    this.sluStudent.slu_cohorts = this.sluStudent.slu_cohorts.map((cohort) => {
                        for (let module of cohort.modules) {
                            if (response.data.slu_module_id === module.id && module.user_id === response.data.user_id) {
                                module.slu_module_records[0] = response.data;
                            }
                        }
                        return cohort;
                    });
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        getSluModuleRecord(module) {
            if (module.slu_module_records[0]) {
                if (
                    module.slu_module_records[0].slu_status_tag !== null &&
                    module.slu_module_records[0].slu_status_tag_id !== null
                ) {
                    return this.statusOptions.find((tag) => tag.id === module.slu_module_records[0].slu_status_tag_id);
                } else if (
                    module.slu_module_records[0].slu_status_tag === null &&
                    module.slu_module_records[0].slu_status_tag_id === null &&
                    module.slu_module_records[0].completed === 1
                ) {
                    return this.statusOptions.find((tag) => tag.id === 'default_completed');
                } else {
                    return this.statusOptions.find((tag) => tag.id === 'default_not_completed');
                }
            }

            return this.statusOptions.find((tag) => tag.id === 'default_not_completed');
        },

        // TODO: Move this business logic to back-end
        calculateRequiredSluPoints() {
            if (!this.selectedCohort.totalPoints) {
                return this.$t('slu_dashboard.no_modules_found');
            }

            let points = (this.selectedCohort.totalPoints / 100) * this.selectedCohort.target;

            points = points % 1 === 0 ? points : points.toFixed(0);
            return this.$t('slu_dashboard.points_required_to_pass', {
                points: points,
                points_name: this.sluSchoolSettingsIn.name_points,
            });
        },
    },
};
</script>

<style scoped lang="scss">
.cohort-per-year {
    margin-bottom: map-get($grid-gutter-spacing, 'sixty');
}

.module {
    margin-bottom: $grid-gutter-width;
}

.module-header {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    row-gap: map-get($grid-gutter-spacing, 'fifteen');
    margin-bottom: map-get($grid-gutter-spacing, 'forty-two');

    .component-title {
        margin-bottom: 0;
    }

    .title {
        font-family: $headings-font-family;
        font-weight: $font-weight-bold;
        margin-bottom: 0;
    }

    select {
        margin-bottom: 0;
        padding-right: 35px;
    }
}

.cohort-additional-info {
    margin-bottom: map-get($grid-gutter-spacing, 'forty-two');

    > div {
        margin-bottom: map-get($grid-gutter-spacing, 'ten');
    }

    .label {
        display: inline-block;
        width: 150px;
        margin-bottom: 0;
        margin-right: map-get($grid-gutter-spacing, 'fifteen');
    }

    span:not(.label) {
        color: $text-muted-color;
        font-weight: $font-weight-medium;
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .module-header {
        flex-direction: row;
        column-gap: map-get($grid-gutter-spacing, 'fifteen');
        justify-content: flex-end;

        .title {
            flex: 1 0 100%;
            align-self: center;
        }

        select {
            width: auto;
            max-width: 300px;
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'xxl')) {
    .module-header {
        .title {
            flex: 1 0 calc(100% - 515px);
        }
    }
}
</style>
